
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { currentConfig } from '../config/environment';
import locationService from './locationService';

class DataService {
  constructor() {
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
    this.metrics = {
      requests: 0,
      successes: 0,
      failures: 0,
      cacheHits: 0,
      cacheMisses: 0,
      retries: 0,
      startTime: Date.now()
    };
  }

  // Retry mechanism for failed API calls
  async retryWithBackoff(fn, retries = this.maxRetries) {
    try {
      return await fn();
    } catch (error) {
      if (retries > 0 && this.isRetryableError(error)) {
        await this.delay(this.retryDelay * (this.maxRetries - retries + 1));
        return this.retryWithBackoff(fn, retries - 1);
      }
      throw error;
    }
  }

  // Retry mechanism with retry count tracking
  async retryWithBackoffAndTracking(fn, retries = this.maxRetries) {
    let retryCount = 0;
    
    const attempt = async (remainingRetries) => {
      try {
        return { data: await fn(), retryCount };
      } catch (error) {
        if (remainingRetries > 0 && this.isRetryableError(error)) {
          retryCount++;
          await this.delay(this.retryDelay * (this.maxRetries - remainingRetries + 1));
          return attempt(remainingRetries - 1);
        }
        // Create a proper Error object with retry count information
        const enhancedError = new Error(error.message || 'Request failed');
        enhancedError.retryCount = retryCount;
        enhancedError.originalError = error;
        throw enhancedError;
      }
    };
    
    return attempt(retries);
  }

  // Check if error is retryable
  isRetryableError(error) {
    const retryableStatuses = [408, 429, 500, 502, 503, 504];
    const retryableMessages = ['timeout', 'rate limit', 'network', 'fetch'];
    
    if (error.status && retryableStatuses.includes(error.status)) {
      return true;
    }
    
    if (error.message) {
      return retryableMessages.some(msg => 
        error.message.toLowerCase().includes(msg)
      );
    }
    
    return false;
  }

  // Delay utility
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Get fallback data for a service
  getFallbackData(serviceType) {
    const fallbacks = {
      analytics: {
        rows: [],
        columnHeaders: [],
        totalsForAllResults: {},
        query: {},
        isFallback: true,
        message: 'Using cached data due to API unavailability'
      },
      searchConsole: {
        rows: [],
        totals: { impressions: 0, clicks: 0, ctr: 0 },
        isFallback: true,
        message: 'Using cached data due to API unavailability'
      },
      places: {
        business: { result: {} },
        reviews: { result: { reviews: [], summary: { total: 0, average: 0 } } },
        isFallback: true,
        message: 'Using cached data due to API unavailability'
      },
      gbpReviews: {
        reviews: [],
        summary: { total: 0, average: 0, breakdown: {} },
        locationsIncluded: [],
        isFallback: true,
        message: 'Using cached data due to API unavailability'
      }
    };
    
    return fallbacks[serviceType] || null;
  }

  // Validate API response data
  validateApiResponse(data, serviceType) {
    const validations = {
      analytics: {
        required: ['rows', 'columnHeaders'],
        optional: ['totalsForAllResults', 'query'],
        validate: (data) => {
          if (!Array.isArray(data.rows)) {
            return 'Analytics data must have rows array';
          }
          if (!Array.isArray(data.columnHeaders)) {
            return 'Analytics data must have columnHeaders array';
          }
          if (data.rows.length > 0 && data.columnHeaders.length > 0) {
            // Check if row data matches column headers
            const firstRow = data.rows[0];
            if (Array.isArray(firstRow) && firstRow.length !== data.columnHeaders.length) {
              return 'Row data length must match column headers length';
            }
          }
          return null;
        }
      },
      searchConsole: {
        required: ['rows'],
        optional: ['totals', 'responseAggregationType'],
        validate: (data) => {
          if (!Array.isArray(data.rows)) {
            return 'Search Console data must have rows array';
          }
          
          if (data.rows.length > 0) {
            const firstRow = data.rows[0];
            if (!firstRow || typeof firstRow !== 'object') {
              return 'Search Console rows must be objects';
            }
            // Check for required fields in rows - updated to match actual API response
            const requiredFields = ['keys', 'clicks', 'impressions', 'ctr', 'position'];
            const missingFields = requiredFields.filter(field => !(field in firstRow));
            if (missingFields.length > 0) {
              return `Search Console rows missing required fields: ${missingFields.join(', ')}`;
            }
          }
          return null;
        }
      },
      places: {
        required: ['business', 'reviews'],
        optional: [],
        validate: (data) => {
          if (!data.business || !data.business.result) {
            return 'Places data must have business.result object';
          }
          if (!data.reviews || !data.reviews.result) {
            return 'Places data must have reviews.result object';
          }
          
          // Validate business data
          const business = data.business.result;
          if (!business.name) {
            return 'Business data must have a name';
          }
          
          // Validate reviews data
          const reviews = data.reviews.result;
          if (!Array.isArray(reviews.reviews)) {
            return 'Reviews data must have reviews array';
          }
          
          if (typeof reviews.summary !== 'object') {
            return 'Reviews data must have summary object';
          }
          
          return null;
        }
      },
      gbpReviews: {
        required: ['reviews', 'summary'],
        optional: ['locationsIncluded'],
        validate: (data) => {
          if (!Array.isArray(data.reviews)) {
            return 'GBP reviews data must have reviews array';
          }
          if (typeof data.summary !== 'object' || data.summary === null) {
            return 'GBP reviews data must have summary object';
          }
          return null;
        }
      }
    };

    const validation = validations[serviceType];
    if (!validation) {
      return { isValid: false, error: `Unknown service type: ${serviceType}` };
    }

    // Check required fields
    for (const field of validation.required) {
      if (!(field in data)) {
        return { isValid: false, error: `Missing required field: ${field}` };
      }
    }

    // Run custom validation
    const customError = validation.validate(data);
    if (customError) {
      return { isValid: false, error: customError };
    }

    return { isValid: true, error: null };
  }

  // Sanitize data for storage
  sanitizeData(data, serviceType) {
    try {
      // Deep clone to avoid modifying original data
      const sanitized = JSON.parse(JSON.stringify(data));
      
      // Remove any potentially problematic fields
      const removeFields = ['__proto__', 'constructor', 'prototype'];
      
      const removeProblematicFields = (obj) => {
        if (obj && typeof obj === 'object') {
          for (const field of removeFields) {
            if (field in obj) {
              delete obj[field];
            }
          }
          
          // Recursively clean nested objects
          for (const key in obj) {
            if (obj[key] && typeof obj[key] === 'object') {
              removeProblematicFields(obj[key]);
            }
          }
        }
      };
      
      removeProblematicFields(sanitized);
      
      // Add metadata
      sanitized._metadata = {
        sanitizedAt: new Date().toISOString(),
        serviceType,
        version: '1.0'
      };
      
      return sanitized;
    } catch (error) {
      console.error('Data sanitization failed:', error);
      throw new Error(`Failed to sanitize data: ${error.message}`);
    }
  }

  // Store data in Firebase for the current user
  async storeUserData(userId, serviceType, data) {
    try {
      const userDataRef = doc(db, 'users', userId, 'data', serviceType);
      const now = new Date();
      const expiresAt = new Date(now.getTime() + this.cacheExpiry);
      
      const dataToStore = {
        data: data,
        lastUpdated: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
        version: '1.0' // Add version for future compatibility
      };
      
      await setDoc(userDataRef, dataToStore);
      
      // Also store in localStorage for immediate access
      try {
        const localStorageKey = `${serviceType}_cache`;
        localStorage.setItem(localStorageKey, JSON.stringify({
          ...dataToStore,
          userId: userId // Include userId for validation
        }));
      } catch (localStorageError) {
        console.warn('Failed to store data in localStorage:', localStorageError);
        // Continue with Firebase storage even if localStorage fails
      }
      
    } catch (error) {
      throw new Error(`Failed to store ${serviceType} data: ${error.message}`);
    }
  }

  // Get cached data from Firebase or localStorage
  async getCachedData(userId, serviceType) {
    try {
      // First try localStorage for faster access
      try {
        const localStorageKey = `${serviceType}_cache`;
        const cached = localStorage.getItem(localStorageKey);
        
        if (cached) {
          const parsed = JSON.parse(cached);
          
          // Validate the cached data
          if (parsed.userId === userId && parsed.expiresAt) {
            const expiresAt = new Date(parsed.expiresAt);
            
            // Check if data is still valid
            if (new Date() < expiresAt) {
              console.log(`Using cached ${serviceType} data from localStorage`);
              return parsed.data;
            } else {
              // Data expired, remove from localStorage
              localStorage.removeItem(localStorageKey);
              console.log(`Cached ${serviceType} data expired, removed from localStorage`);
            }
          }
        }
      } catch (localStorageError) {
        console.warn('localStorage access failed:', localStorageError);
      }
      
      // Fallback to Firebase
      const userDataRef = doc(db, 'users', userId, 'data', serviceType);
      const docSnap = await getDoc(userDataRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const expiresAt = new Date(data.expiresAt);
        
        // Check if data is still valid
        if (new Date() < expiresAt) {
          // Update localStorage with fresh data
          try {
            const localStorageKey = `${serviceType}_cache`;
            localStorage.setItem(localStorageKey, JSON.stringify({
              data: data.data,
              lastUpdated: data.lastUpdated,
              expiresAt: data.expiresAt,
              userId: userId,
              version: data.version || '1.0'
            }));
          } catch (localStorageError) {
            console.warn('Failed to update localStorage:', localStorageError);
          }
          
          return data.data;
        } else {
          // Data expired, remove from Firebase
          try {
            await setDoc(userDataRef, { data: null, lastUpdated: new Date().toISOString() });
          } catch (cleanupError) {
            console.warn('Failed to cleanup expired data:', cleanupError);
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error(`Failed to get cached ${serviceType} data:`, error);
      return null;
    }
  }

  // Clear cache for a specific service
  async clearServiceCache(userId, serviceType) {
    try {
      // Clear from Firebase
      const userDataRef = doc(db, 'users', userId, 'data', serviceType);
      await setDoc(userDataRef, { data: null, lastUpdated: new Date().toISOString() });
      
      // Clear from localStorage
      try {
        const localStorageKey = `${serviceType}_cache`;
        localStorage.removeItem(localStorageKey);
      } catch (localStorageError) {
        console.warn('Failed to clear localStorage cache:', localStorageError);
      }
      
      console.log(`Cache cleared for ${serviceType}`);
    } catch (error) {
      console.error(`Failed to clear ${serviceType} cache:`, error);
    }
  }

  // Clear all caches for a user
  async clearAllCaches(userId) {
    const services = ['analytics', 'searchConsole', 'places', 'gbpReviews'];
    
    for (const service of services) {
      await this.clearServiceCache(userId, service);
    }
    
    console.log('All caches cleared');
  }

  // Fetch Google Business Profile reviews (all linked locations) and store them
  async fetchAndStoreGbpReviewsData(userId, accountId) {
    const startTime = Date.now();

    try {
      if (!accountId) {
        throw new Error('accountId is required for GBP reviews');
      }

      const portalLocations = await locationService.getLocations(accountId);
      const linkedLocations = portalLocations.filter((l) => l.gbpAccountName && l.gbpLocationName);

      if (linkedLocations.length === 0) {
        const empty = {
          reviews: [],
          summary: { total: 0, average: 0, breakdown: {} },
          locationsIncluded: [],
          isFallback: false,
        };
        const sanitizedEmpty = this.sanitizeData(empty, 'gbpReviews');
        await this.storeUserData(userId, 'gbpReviews', sanitizedEmpty);
        return sanitizedEmpty;
      }

      const fetchLocationReviews = async (location) => {
        const url = `${currentConfig.WORKER_URL}/api/gbp/reviews?locationName=${encodeURIComponent(location.gbpLocationName)}&userId=${encodeURIComponent(userId)}&accountId=${encodeURIComponent(accountId)}&pageSize=50&orderBy=${encodeURIComponent('updateTime desc')}`;
        const res = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
        if (!res.ok) {
          const errorText = await res.text().catch(() => 'Unknown error');
          const err = new Error(`GBP reviews error: ${res.status} - ${errorText}`);
          err.status = res.status;
          err.responseText = errorText;
          throw err;
        }
        return await res.json();
      };

      const results = await Promise.allSettled(
        linkedLocations.map(async (loc) => ({ loc, data: await this.retryWithBackoff(() => fetchLocationReviews(loc)) }))
      );

      const aggregated = [];
      const locationsIncluded = [];
      const errors = [];

      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          const { loc, data } = r.value;
          locationsIncluded.push({
            id: loc.id,
            name: loc.name || null,
            gbpAccountName: loc.gbpAccountName,
            gbpLocationName: loc.gbpLocationName,
            totalReviewCount: data.totalReviewCount ?? null,
            averageRating: data.averageRating ?? null,
          });

          const reviews = data.reviews || [];
          reviews.forEach((rev) => {
            const rating = rev.starRating ?? rev.rating ?? null;
            const comment = rev.comment || rev.reviewText || rev.text || '';
            const authorName = rev.reviewer?.displayName || rev.reviewer?.name || rev.reviewer?.profileName || 'Anonymous';
            const updateTime = rev.updateTime || rev.createTime || null;

            aggregated.push({
              id: rev.name || rev.reviewId || `${loc.gbpLocationName}:${rev.createTime || rev.updateTime || Math.random()}`,
              reviewName: rev.name || null,
              authorName,
              rating: typeof rating === 'number' ? rating : rating ? Number(rating) : null,
              comment,
              createTime: rev.createTime || null,
              updateTime,
              reply: rev.reviewReply?.comment || null,
              locationId: loc.id,
              locationName: loc.name || null,
              gbpLocationName: loc.gbpLocationName,
            });
          });
        } else {
          errors.push(r.reason?.message || String(r.reason));
        }
      });

      // Compute summary from aggregated data (not all GBP reviews may be returned if paginated)
      const breakdown = {};
      let ratingSum = 0;
      let ratingCount = 0;
      aggregated.forEach((rev) => {
        if (typeof rev.rating === 'number' && !Number.isNaN(rev.rating)) {
          ratingSum += rev.rating;
          ratingCount += 1;
          const key = String(Math.round(rev.rating));
          breakdown[key] = (breakdown[key] || 0) + 1;
        }
      });

      const transformed = {
        reviews: aggregated,
        summary: {
          total: aggregated.length,
          average: ratingCount ? Math.round((ratingSum / ratingCount) * 10) / 10 : 0,
          breakdown,
        },
        locationsIncluded,
        isFallback: false,
        errors: errors.length ? errors : undefined,
      };

      const validation = this.validateApiResponse(transformed, 'gbpReviews');
      if (!validation.isValid) {
        throw new Error(`GBP reviews data validation failed: ${validation.error}`);
      }

      const sanitized = this.sanitizeData(transformed, 'gbpReviews');
      await this.storeUserData(userId, 'gbpReviews', sanitized);

      const duration = Date.now() - startTime;
      this.logMetrics('fetchGbpReviews', 'gbpReviews', duration, true, false, 0);

      return sanitized;
    } catch (error) {
      const duration = Date.now() - startTime;
      const retryCount = error.retryCount || 0;
      this.logMetrics('fetchGbpReviews', 'gbpReviews', duration, false, false, retryCount);

      console.error('GBP reviews fetch failed:', error);

      try {
        const cached = await this.getCachedData(userId, 'gbpReviews');
        if (cached) {
          return { ...cached, isFallback: true, message: 'Using cached data due to API error' };
        }
      } catch (cacheError) {
        console.error('Failed to get cached GBP reviews:', cacheError);
      }

      return this.getFallbackData('gbpReviews');
    }
  }

  // Fetch Analytics data and store it
  async fetchAndStoreAnalyticsData(userId, accountId, propertyId) {
    const startTime = Date.now();
    
    try {
      // Extract the actual property ID from the data stream ID if needed
      let cleanPropertyId = propertyId;
      if (propertyId && propertyId.includes('/')) {
        // If it's a data stream ID like "properties/375456791/dataStreams/5192442157"
        // Extract just the property ID "375456791"
        const match = propertyId.match(/properties\/(\d+)/);
        if (match) {
          cleanPropertyId = match[1];
        }
      }

      // Get Analytics data for the last 90 days via Worker (extended range for better data)
      const endDate = new Date();
      const startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
      
      const fetchAnalytics = async () => {
        // For account scoping: userId should be the admin's UID, accountId should be the client's account ID
        const url = `${currentConfig.WORKER_URL}/api/ga/report?propertyId=${cleanPropertyId}&startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}&userId=${encodeURIComponent(userId)}${accountId ? `&accountId=${encodeURIComponent(accountId)}` : ''}`;
        
        console.log('🔍 Fetching Analytics data from:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => 'Unknown error');
          const error = new Error(`Analytics API error: ${response.status} - ${errorText}`);
          error.status = response.status;
          error.responseText = errorText;
          console.error('Analytics API error details:', {
            status: response.status,
            statusText: response.statusText,
            url: url,
            errorText: errorText
          });
          throw error;
        }

        const data = await response.json();
        console.log('🔍 Analytics API response received:', data);
        return data;
      };

      // Use retry mechanism with tracking
      const { data, retryCount: fetchedRetryCount } = await this.retryWithBackoffAndTracking(fetchAnalytics);
      
      // Transform the data to match our expected format and make it Firebase-compatible
      const transformedData = {
        rows: data.rows || [],
        columnHeaders: data.columnHeaders || [],
        totalsForAllResults: data.totalsForAllResults || {},
        changes: data.changes || {}, // Preserve the changes data for trend analysis
        query: data.query || {},
        isFallback: false
      };
      
      // Convert nested arrays to objects for Firebase compatibility
      if (transformedData.rows && Array.isArray(transformedData.rows)) {
        transformedData.rows = transformedData.rows.map((row, index) => {
          if (Array.isArray(row)) {
            // Convert array to object with named properties
            const rowObj = {};
            if (transformedData.columnHeaders) {
              transformedData.columnHeaders.forEach((header, colIndex) => {
                rowObj[header.name] = row[colIndex];
              });
            }
            rowObj._rowIndex = index; // Add index for reference
            return rowObj;
          }
          return row;
        });
      }

      // Validate the transformed data
      const validation = this.validateApiResponse(transformedData, 'analytics');
      if (!validation.isValid) {
        throw new Error(`Analytics data validation failed: ${validation.error}`);
      }

      // Sanitize the data before storage
      const sanitizedData = this.sanitizeData(transformedData, 'analytics');

      // Store the data
      await this.storeUserData(userId, 'analytics', sanitizedData);
      
      const duration = Date.now() - startTime;
      this.logMetrics('fetchAnalytics', 'analytics', duration, true, false, fetchedRetryCount);
      
      return sanitizedData;
    } catch (error) {
      const duration = Date.now() - startTime;
      const retryCount = error.retryCount || 0;
      this.logMetrics('fetchAnalytics', 'analytics', duration, false, false, retryCount);
      
      console.error('Analytics data fetch failed:', error);
      
      // Try to get cached data as fallback
      try {
        const cachedData = await this.getCachedData(userId, 'analytics');
        if (cachedData) {
          console.log('Using cached Analytics data as fallback');
          this.logMetrics('getCachedData', 'analytics', 0, true, true, 0);
          return { 
            ...cachedData, 
            isFallback: true, 
            message: 'Using cached data due to API error',
            error: {
              type: 'api_error',
              status: error.status,
              message: error.message,
              details: error.responseText || 'Google Analytics API is currently unavailable'
            }
          };
        }
      } catch (cacheError) {
        console.error('Failed to get cached Analytics data:', cacheError);
      }
      
      // Return fallback data if no cache available
      const fallbackData = this.getFallbackData('analytics');
      return {
        ...fallbackData,
        error: {
          type: 'api_error',
          status: error.status,
          message: error.message,
          details: error.responseText || 'Google Analytics API is currently unavailable. Please check your connection and try again.'
        }
      };
    }
  }

  // Fetch Search Console data and store it
  async fetchAndStoreSearchConsoleData(userId, propertyUrl, accountId = null) {
    const startTime = Date.now();
    
    try {
      console.log('🔍 Fetching Search Console data for:', propertyUrl);

      // Get Search Console keywords data for the last year via Worker (extended range for keyword data)
      const url = `${currentConfig.WORKER_URL}/api/sc/keywords?siteUrl=${encodeURIComponent(propertyUrl)}&userId=${encodeURIComponent(userId)}${accountId ? `&accountId=${encodeURIComponent(accountId)}` : ''}`;
      
      const response = await fetch(url, {
        method: 'POST',
          headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const error = new Error(`Search Console API error: ${response.status}`);
        console.error('Search Console API error:', error);
        throw error;
      }

      const data = await response.json();
      console.log('🔍 Raw Search Console response:', data);
      
      // Validate the response data
      const validation = this.validateApiResponse(data, 'searchConsole');
      if (!validation.isValid) {
        throw new Error(`Search Console data validation failed: ${validation.error}`);
      }

      // Sanitize the data before storage
      const sanitizedData = this.sanitizeData(data, 'searchConsole');
      
      // Store the data
      await this.storeUserData(userId, 'searchConsole', sanitizedData);
      
      const duration = Date.now() - startTime;
      this.logMetrics('fetchSearchConsole', 'searchConsole', duration, true, false, 0);
      
      return sanitizedData;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logMetrics('fetchSearchConsole', 'searchConsole', duration, false, false, 0);
      
      console.error('Search Console data fetch failed:', error);
      
      // Try to get cached data as fallback
      try {
        const cachedData = await this.getCachedData(userId, 'searchConsole');
        if (cachedData) {
          console.log('Using cached Search Console data as fallback');
          this.logMetrics('getCachedData', 'searchConsole', 0, true, true, 0);
          return { 
            ...cachedData, 
            isFallback: true, 
            message: 'Using cached data due to API error',
            error: {
              type: 'api_error',
              status: error.status,
              message: error.message,
              details: 'Google Search Console API is currently unavailable'
            }
          };
        }
      } catch (cacheError) {
        console.error('Failed to get cached Search Console data:', cacheError);
      }
      
      // Return fallback data if no cache available
      const fallbackData = this.getFallbackData('searchConsole');
      return {
        ...fallbackData,
        error: {
          type: 'api_error',
          status: error.status,
          message: error.message,
          details: 'Google Search Console API is currently unavailable. Please check your connection and try again.'
        }
      };
    }
  }

  // Fetch Google Places data and store it
  async fetchAndStorePlacesData(userId, businessUrl) {
    try {
      // Determine the place ID to use
      let finalPlaceId = null;
      
      if (businessUrl.includes('place_id=')) {
        // Extract place ID from business URL if it contains place_id parameter
        const match = businessUrl.match(/place_id=([^&]+)/);
        if (match) {
          finalPlaceId = match[1];
        }
      } else if (businessUrl.includes('maps.app.goo.gl') || businessUrl.includes('google.com/maps')) {
        // This is a Google Maps URL - search for it first to get place ID
        console.log('Searching for business from Google Maps URL:', businessUrl);
        
        const searchPlaces = async () => {
          const searchResponse = await fetch(
            `${currentConfig.WORKER_URL}/api/places/search?query=${encodeURIComponent(businessUrl)}`,
            {
              method: 'POST',
          headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ textQuery: businessUrl })
            }
          );
          
          if (!searchResponse.ok) {
            const error = new Error(`Places search error: ${searchResponse.status}`);
            error.status = searchResponse.status;
            throw error;
          }
          
          return await searchResponse.json();
        };

        const searchData = await this.retryWithBackoff(searchPlaces);
        
        if (!searchData.places || searchData.places.length === 0) {
          throw new Error('No business found with the provided URL');
        }
        
        // Use the first result's place ID
        finalPlaceId = searchData.places[0].id;
        
        // If we got a place ID from search, we can also get the business details from the search result
        if (searchData.places[0].displayName && searchData.places[0].formattedAddress) {
          // We already have business details from search, so we can skip the details call
          const businessData = searchData.places[0];
          
          // Get reviews from Places API (New)
          const fetchReviews = async () => {
      const reviewsResponse = await fetch(
              `${currentConfig.WORKER_URL}/api/places/reviews?placeId=${encodeURIComponent(finalPlaceId)}`,
              {
                method: 'GET',
              }
            );

            if (!reviewsResponse.ok) {
              const error = new Error(`Places reviews error: ${reviewsResponse.status}`);
              error.status = reviewsResponse.status;
              throw error;
            }

            return await reviewsResponse.json();
          };

          let reviewsData = null;
          try {
            reviewsData = await this.retryWithBackoff(fetchReviews);
          } catch (reviewsError) {
            console.warn('Failed to fetch reviews, continuing without them:', reviewsError);
          }

          // Transform the data to match our expected format
          const transformedData = {
            business: {
              result: {
                name: businessData.displayName?.text || 'Business Name',
                address: businessData.formattedAddress || 'Address not available',
                phone: businessData.internationalPhoneNumber || 'Phone not available',
                website: businessData.websiteUri || 'Website not available',
                rating: businessData.rating || 0,
                totalReviews: businessData.userRatingCount || 0,
                openingHours: businessData.regularOpeningHours || null,
                photos: businessData.photos || [],
                types: businessData.types || []
              }
            },
            reviews: {
              result: {
                reviews: reviewsData?.reviews || [],
                summary: {
                  total: reviewsData?.reviews?.length || 0,
                  average: businessData.rating || 0,
                  breakdown: {} // New API doesn't provide rating breakdown
                }
              }
            },
            isFallback: false
          };

          // Validate the transformed data
          const validation = this.validateApiResponse(transformedData, 'places');
          if (!validation.isValid) {
            throw new Error(`Places data validation failed: ${validation.error}`);
          }

          // Sanitize the data before storage
          const sanitizedData = this.sanitizeData(transformedData, 'places');

          // Store the data
          await this.storeUserData(userId, 'places', sanitizedData);
          
          return sanitizedData;
        }
      } else {
        // This is a Place ID - use it directly to get business details and reviews
        console.log('Using Place ID directly:', businessUrl);
        
        const fetchPlacesData = async () => {
          // Get business details and reviews using the Place ID
          const response = await fetch(
            `${currentConfig.WORKER_URL}/api/places/details?placeId=${encodeURIComponent(businessUrl)}&userId=${encodeURIComponent(userId)}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              }
            }
          );

          if (!response.ok) {
            const error = new Error(`Places API error: ${response.status}`);
            error.status = response.status;
            throw error;
          }

          const placesData = await response.json();
          console.log('Places data result:', placesData);
          
          // Transform the data to match our expected format
          const transformedData = {
            business: {
              result: {
                name: placesData.displayName?.text || 'Business Name',
                address: placesData.formattedAddress || 'Address not available',
                phone: placesData.internationalPhoneNumber || 'Phone not available',
                website: placesData.websiteUri || 'Website not available',
                rating: placesData.rating || 0,
                totalReviews: placesData.userRatingCount || 0,
                openingHours: placesData.regularOpeningHours || null,
                photos: placesData.photos || [],
                types: placesData.types || []
              }
            },
            reviews: {
              result: {
                reviews: placesData.reviews || [],
                summary: {
                  total: placesData.reviews?.length || 0,
                  average: placesData.rating || 0,
                  breakdown: {} // New API doesn't provide rating breakdown
                }
              }
            },
            isFallback: false
          };

          // Validate the transformed data
          const validation = this.validateApiResponse(transformedData, 'places');
          if (!validation.isValid) {
            throw new Error(`Places data validation failed: ${validation.error}`);
          }

          // Sanitize the data before storage
          const sanitizedData = this.sanitizeData(transformedData, 'places');

      // Store the data
          await this.storeUserData(userId, 'places', sanitizedData);
          
          return sanitizedData;
        };

        return await this.retryWithBackoff(fetchPlacesData);
      }


    } catch (error) {
      console.error('Places data fetch failed:', error);
      
      // Try to get cached data as fallback
      try {
        const cachedData = await this.getCachedData(userId, 'places');
        if (cachedData) {
          console.log('Using cached Places data as fallback');
          return { ...cachedData, isFallback: true, message: 'Using cached data due to API error' };
        }
      } catch (cacheError) {
        console.error('Failed to get cached Places data:', cacheError);
      }
      
      // Return fallback data if no cache available
      return this.getFallbackData('places');
    }
  }

  // Get data for a specific service (cached or fresh)
  async getAnalyticsData(userId, accountId, propertyId) {
    try {
      // First try to get cached data
      let data = await this.getCachedData(userId, 'analytics');
      
      if (!data) {
        // If no cached data, fetch fresh data
        data = await this.fetchAndStoreAnalyticsData(userId, accountId, propertyId);
      }
      
      return data;
    } catch (error) {
      throw new Error(`Failed to get Analytics data: ${error.message}`);
    }
  }

  async getSearchConsoleData(userId, propertyUrl) {
    try {
      // First try to get cached data
      let data = await this.getCachedData(userId, 'searchConsole');
      
      if (!data) {
        // If no cached data, fetch fresh data
        data = await this.fetchAndStoreSearchConsoleData(userId, propertyUrl);
      }
      
      return data;
    } catch (error) {
      throw new Error(`Failed to get Search Console data: ${error.message}`);
    }
  }

  async getPlacesData(userId, businessUrl) {
    try {
      // First try to get cached data
      let data = await this.getCachedData(userId, 'places');
      
      if (!data) {
        // If no cached data, fetch fresh data
        data = await this.fetchAndStorePlacesData(userId, businessUrl);
      }
      
      return data;
    } catch (error) {
      throw new Error(`Failed to get Places data: ${error.message}`);
    }
  }

  async getGbpReviewsData(userId, accountId) {
    try {
      let data = await this.getCachedData(userId, 'gbpReviews');

      if (!data) {
        data = await this.fetchAndStoreGbpReviewsData(userId, accountId);
      }

      return data;
    } catch (error) {
      throw new Error(`Failed to get GBP reviews data: ${error.message}`);
    }
  }

  // Force refresh data for a service
  async refreshData(userId, serviceType, ...params) {
    try {
      switch (serviceType) {
        case 'analytics':
          // For analytics: userId (admin UID), accountId (client account ID), propertyId
          return await this.fetchAndStoreAnalyticsData(userId, params[1], params[0]);
        case 'searchConsole':
          // For search console: userId (admin UID), propertyUrl, accountId (client account ID)
          return await this.fetchAndStoreSearchConsoleData(userId, params[0], params[1]);
        case 'places':
          return await this.fetchAndStorePlacesData(userId, ...params);
        case 'gbpReviews':
          // For GBP reviews: userId (admin UID), accountId (client account ID)
          return await this.fetchAndStoreGbpReviewsData(userId, params[0]);
        default:
          throw new Error(`Unknown service type: ${serviceType}`);
      }
    } catch (error) {
      throw new Error(`Failed to refresh ${serviceType} data: ${error.message}`);
    }
  }

  // Log metrics and performance data
  logMetrics(operation, serviceType, duration, success, cacheHit = false, retryCount = 0) {
    this.metrics.requests++;
    
    if (success) {
      this.metrics.successes++;
    } else {
      this.metrics.failures++;
    }
    
    if (cacheHit) {
      this.metrics.cacheHits++;
    } else {
      this.metrics.cacheMisses++;
    }
    
    if (retryCount > 0) {
      this.metrics.retries += retryCount;
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`📊 Data Service Metrics - ${operation}`);
      console.log(`Service: ${serviceType}`);
      console.log(`Duration: ${duration}ms`);
      console.log(`Success: ${success}`);
      console.log(`Cache Hit: ${cacheHit}`);
      console.log(`Retries: ${retryCount}`);
      console.groupEnd();
    }

    // Send metrics to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      this.sendMetricsToService({
        operation,
        serviceType,
        duration,
        success,
        cacheHit,
        retryCount,
        timestamp: new Date().toISOString()
      });
    }
  }

  // Send metrics to external monitoring service
  async sendMetricsToService(metricData) {
    try {
      // Only send metrics if we have a valid metrics endpoint configured
      const metricsEndpoint = process.env.REACT_APP_METRICS_ENDPOINT;
      if (!metricsEndpoint) {
        console.log('Metrics endpoint not configured, skipping metrics submission');
        return;
      }

      // In production, send to your monitoring service (DataDog, New Relic, etc.)
      await fetch(metricsEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(metricData)
      });
    } catch (error) {
      console.warn('Failed to send metrics to monitoring service:', error);
    }
  }

  // Get current metrics
  getMetrics() {
    const uptime = Date.now() - this.metrics.startTime;
    const successRate = this.metrics.requests > 0 
      ? (this.metrics.successes / this.metrics.requests * 100).toFixed(2)
      : 0;
    const cacheHitRate = (this.metrics.cacheHits + this.metrics.cacheMisses) > 0
      ? (this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses) * 100).toFixed(2)
      : 0;

    return {
      ...this.metrics,
      uptime,
      successRate: `${successRate}%`,
      cacheHitRate: `${cacheHitRate}%`,
      averageResponseTime: this.metrics.requests > 0 
        ? (uptime / this.metrics.requests).toFixed(2)
        : 0
    };
  }

  // Reset metrics
  resetMetrics() {
    this.metrics = {
      requests: 0,
      successes: 0,
      failures: 0,
      cacheHits: 0,
      cacheMisses: 0,
      retries: 0,
      startTime: Date.now()
    };
  }
}

const dataService = new DataService();

export default dataService;
